import { cn } from "@/lib/utils";

const DEFAULT_BRAND_NAME = "TechAlign";

/**
 * Color context for the two-tone TechAlign wordmark.
 * - `theme`: follows the active theme (foreground/muted-foreground) — for the app header.
 * - `on-dark`: white tones for placement on a dark panel (login hero).
 * - `on-light`: slate tones for placement on a light panel (login form).
 */
type WordmarkTone = "theme" | "on-dark" | "on-light";

// Weights mirror the public site: "Tech" 400 (muted), "Align" 600 (strong).
const TONE_CLASSES: Record<WordmarkTone, { tech: string; align: string }> = {
  theme: {
    tech: "font-normal text-muted-foreground",
    align: "font-semibold text-foreground",
  },
  "on-dark": {
    tech: "font-normal text-white/70",
    align: "font-semibold text-white",
  },
  "on-light": {
    tech: "font-normal text-slate-400",
    align: "font-semibold text-slate-900",
  },
};

/** Single-tone styling used for custom (white-label) brand names — preserves
 *  the prior system-font appearance for each context. */
const CUSTOM_NAME_CLASSES: Record<WordmarkTone, string> = {
  theme: "font-semibold text-foreground",
  "on-dark": "font-bold text-white",
  "on-light": "font-bold text-slate-900",
};

interface WordmarkProps {
  /** Brand name to render. When it equals the default "TechAlign" the two-tone
   *  split is applied; any other (white-label) name renders single-tone. */
  name?: string | null;
  /** Color context so the wordmark stays legible on its background. */
  tone?: WordmarkTone;
  /** Extra classes for sizing (e.g. text-3xl) and layout. */
  className?: string;
}

export function Wordmark({ name, tone = "theme", className }: WordmarkProps) {
  const brandName = name?.trim() || DEFAULT_BRAND_NAME;
  const tones = TONE_CLASSES[tone];

  if (brandName !== DEFAULT_BRAND_NAME) {
    return (
      <span
        className={cn(CUSTOM_NAME_CLASSES[tone], className)}
        data-testid="text-wordmark"
      >
        {brandName}
      </span>
    );
  }

  return (
    <span
      className={cn("font-wordmark tracking-tight", className)}
      data-testid="text-wordmark"
    >
      <span className={tones.tech}>Tech</span>
      <span className={tones.align}>Align</span>
    </span>
  );
}
